/**
 * Error Handling Utilities
 * Provides structured error handling and logging for the application
 */

import { FEATURE_FLAGS } from '../config';

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(message, statusCode, details = {}) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date().toISOString();

    // Maintains proper stack trace for where error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      details: this.details,
      timestamp: this.timestamp
    };
  }
}

/**
 * Custom Network Error class
 */
export class NetworkError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'NetworkError';
    this.details = details;
    this.timestamp = new Date().toISOString();

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      details: this.details,
      timestamp: this.timestamp
    };
  }
}

/**
 * Custom Validation Error class
 */
export class ValidationError extends Error {
  constructor(message, field, details = {}) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.details = details;
    this.timestamp = new Date().toISOString();

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ValidationError);
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      field: this.field,
      details: this.details,
      timestamp: this.timestamp
    };
  }
}

/**
 * Log error to console and optionally to external service
 * @param {Error} error - The error to log
 * @param {Object} context - Additional context about the error
 */
export const logError = (error, context = {}) => {
  const errorInfo = {
    message: error.message,
    name: error.name,
    stack: error.stack,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Always log to console
  console.error('Error logged:', errorInfo);

  // Log to external service if enabled (e.g., Sentry)
  if (FEATURE_FLAGS.ENABLE_ERROR_TRACKING && window.Sentry) {
    try {
      window.Sentry.captureException(error, {
        extra: context,
        tags: {
          errorType: error.name
        }
      });
    } catch (sentryError) {
      console.error('Failed to log error to Sentry:', sentryError);
    }
  }

  // Could also send to custom logging endpoint
  if (process.env.NODE_ENV === 'production') {
    try {
      // Send to logs API (non-blocking)
      fetch('/api/client-errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorInfo)
      }).catch(() => {
        // Silently fail - don't break app if logging fails
      });
    } catch (e) {
      // Silently fail
    }
  }

  return errorInfo;
};

/**
 * Handle errors with user-friendly messages
 * @param {Error} error - The error to handle
 * @param {Function} setError - State setter for error display
 * @param {Object} context - Additional context
 */
export const handleError = (error, setError, context = {}) => {
  logError(error, context);

  // Determine user-friendly message
  let userMessage = 'An unexpected error occurred. Please try again.';

  if (error instanceof ApiError) {
    if (error.statusCode === 404) {
      userMessage = 'The requested resource was not found.';
    } else if (error.statusCode === 401 || error.statusCode === 403) {
      userMessage = 'Authentication failed. Please refresh and try again.';
    } else if (error.statusCode >= 500) {
      userMessage = 'Server error. Please try again in a moment.';
    } else {
      userMessage = error.message || userMessage;
    }
  } else if (error instanceof NetworkError) {
    userMessage = 'Network connection failed. Please check your internet connection.';
  } else if (error instanceof ValidationError) {
    userMessage = error.message;
  } else if (error.name === 'AbortError') {
    userMessage = 'Request timed out. Please try again.';
  }

  // Set error for display
  if (setError) {
    setError({
      message: userMessage,
      technical: process.env.NODE_ENV === 'development' ? error.message : undefined,
      timestamp: new Date().toISOString()
    });
  }

  return userMessage;
};

/**
 * Async error wrapper for promises
 * @param {Promise} promise - Promise to wrap
 * @param {Function} errorHandler - Optional custom error handler
 * @returns {Promise} - [error, data] tuple
 */
export const asyncErrorHandler = async (promise, errorHandler) => {
  try {
    const data = await promise;
    return [null, data];
  } catch (error) {
    if (errorHandler) {
      errorHandler(error);
    } else {
      logError(error);
    }
    return [error, null];
  }
};

/**
 * Create an error boundary handler for React
 */
export class ErrorBoundary {
  constructor() {
    this.hasError = false;
    this.error = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logError(error, { componentStack: errorInfo.componentStack });
  }
}

/**
 * Format error for display
 * @param {Error} error - Error to format
 * @returns {Object} - Formatted error object
 */
export const formatError = (error) => {
  if (!error) {
    return null;
  }

  return {
    title: getErrorTitle(error),
    message: getErrorMessage(error),
    suggestions: getErrorSuggestions(error),
    technical: process.env.NODE_ENV === 'development' ? {
      name: error.name,
      message: error.message,
      stack: error.stack
    } : null
  };
};

/**
 * Get user-friendly error title
 */
const getErrorTitle = (error) => {
  if (error instanceof NetworkError) {
    return 'Connection Error';
  }
  if (error instanceof ApiError) {
    if (error.statusCode >= 500) {
      return 'Server Error';
    }
    if (error.statusCode === 404) {
      return 'Not Found';
    }
    if (error.statusCode === 401 || error.statusCode === 403) {
      return 'Authentication Error';
    }
    return 'Request Error';
  }
  if (error instanceof ValidationError) {
    return 'Validation Error';
  }
  return 'Error';
};

/**
 * Get user-friendly error message
 */
const getErrorMessage = (error) => {
  if (error.message && error.message.length < 100) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get suggested actions for error
 */
const getErrorSuggestions = (error) => {
  if (error instanceof NetworkError) {
    return [
      'Check your internet connection',
      'Try again in a moment',
      'Refresh the page'
    ];
  }
  if (error instanceof ApiError) {
    if (error.statusCode >= 500) {
      return [
        'Wait a moment and try again',
        'The server may be experiencing issues',
        'Contact support if the problem persists'
      ];
    }
    if (error.statusCode === 401 || error.statusCode === 403) {
      return [
        'Refresh the page',
        'Clear your browser cache',
        'Try reconnecting to the WiFi network'
      ];
    }
  }
  return [
    'Refresh the page and try again',
    'Check your internet connection',
    'Contact support if the issue continues'
  ];
};
